import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, take } from 'rxjs';

import { routes } from './app.routes';
import { provideUnsavedChangesGuard } from './shared/components/unsaved-changes-dialog/unsaved-changes-dialog';
import { authInterceptor } from './core/interceptors/auth-interceptor';
import { errorInterceptor } from './core/interceptors/error-interceptor';
import { loaderInterceptor } from './core/interceptors/loader-interceptor';

/** App-wide unsaved-changes prompt (Save / Close / Save & Close). Set to true to turn it on; no code to uncomment. */
//unsave prompts

const UNSAVED_CHANGES_PROMPT_ENABLED = false;

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideUnsavedChangesGuard({ enabled: UNSAVED_CHANGES_PROMPT_ENABLED }),
    provideHttpClient(withInterceptors([authInterceptor, loaderInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
    provideTranslateService({ fallbackLang: 'en', lang: 'en' }),
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const lang = localStorage.getItem('lang') || 'en';

      const activated = firstValueFrom(translate.onLangChange.pipe(take(1)));
      translate.use(lang);
      await activated;

      // Preload the fallback language too, so keys missing from an
      // incomplete/active locale file still resolve instead of showing raw keys.
      if (lang !== 'en') {
        await firstValueFrom(translate.reloadLang('en'));
      }
    }),
  ],
};
